import { GridColDef } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { City } from "../models/cities.model";
import { ListItem } from "../models/ListItem";
import { cachedMasterDataService } from "../service/cachedMasterData.service";
import { citiesService } from "../service/cities.service";
import { rmcService } from "../service/rmcService";
// import DataTable from 'react-data-table-component';
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Form, Offcanvas } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import { Rmc } from "../models/rmc.model";
import { countryService } from "../service/country.service";
import { statesService } from "../service/state.service";
import SharedAutoComplete from "./Controls/SharedAutoComplete";
import PageHeader from "./PageHeader";
import "./RmcGrid.css";
import RMCImportDrawer from "./RMCImportDrawer";

// interface Rmc {
//   name: string;
//   code: string;
//   description: string;
//   address: string;
//   cityId: number;
//   zipCode: string;
// }
type DropdownOption = { label: string; value: number };

const columns: GridColDef[] = [
  { field: "name", headerName: "Name", flex: 1 },
  { field: "code", headerName: "Code", flex: 1 },
  { field: "description", headerName: "Description", flex: 2 },
  { field: "address", headerName: "Address", flex: 2 },
  { field: "city", headerName: "City", flex: 1 },
  { field: "zipCode", headerName: "ZipCode", flex: 1 },
];

const RmcGrid: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [rmcCities, setRmcCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<number | null>(null);
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [states, setStates] = useState<DropdownOption[]>([]);
  const [selectedCity, setSelectedCity] = useState<ListItem | null>(null);

  // Input text states for autocomplete to allow typing
  const [countryInputText, setCountryInputText] = useState<string>("");
  const [stateInputText, setStateInputText] = useState<string>("");
  const [cityInputText, setCityInputText] = useState<string>("");

  // Filtered suggestions for autocomplete
  const [filteredCountries, setFilteredCountries] = useState<DropdownOption[]>(
    []
  );
  const [filteredStates, setFilteredStates] = useState<DropdownOption[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [selectedRmc, setSelectedRmc] = useState<Rmc | null>(null);
  const [selectedType, setSelectedType] = useState<{
    label: string;
    value: string;
  } | null>(null);
  
  // Backend filter for Type selection
  const [filterTypeValue, setFilterTypeValue] = useState<string | null>(null);

  const [rmcs, setRmcs] = useState<Rmc[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"view" | "edit" | "create">(
    "view"
  );
  const [editingRmcId, setEditingRmcId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const savedPage = localStorage.getItem("rmcTablePage");
    return savedPage ? parseInt(savedPage) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState<number>(() => {
    const savedRows = localStorage.getItem("rmcTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const [form, setForm] = useState<Omit<Rmc, "city"> & { cityId: number }>({
    name: "",
    code: "",
    description: "",
    address: "",
    cityId: 0,
    zipCode: "",
    aggregated: false, // Default to false for RMC
  });
  // Add state for type
  const [type, setType] = useState<"" | "RMC" | "Aggregator">("RMC");
  const [viewCityName, setViewCityName] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [rmcsLoaded, setRmcsLoaded] = useState(false);
  const [loadingRmcs, setLoadingRmcs] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Server-side pagination state
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 0,
    sortField: "name",
    sortOrder: 1,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [usePagination, setUsePagination] = useState(true);

  const navigate = useNavigate();

  const loadRmcDataPaginated = async (
    page: number = lazyState.page,
    pageSize: number = lazyState.rows,
    sortField: string = lazyState.sortField,
    sortOrder: number = lazyState.sortOrder,
    filter: string = globalFilter
  ) => {
    setLoadingRmcs(true);
    try {
      const filters: any = {
        pageNumber: page + 1, // API expects 1-based page numbers
        pageSize: pageSize,
        sortField: sortField,
        sortOrder: sortOrder === 1 ? "asc" : "desc",
        globalFilter: filter,
        isActive: true,
      };
      
      // Add Type filter if selected
      if (filterTypeValue) {
        filters.aggregated = filterTypeValue === "Aggregator";
      }
      
      const result = await rmcService.getAllPaginated<Rmc>(filters);

      if (result) {
        setRmcs(result.items ?? []);
        setTotalRecords(result.totalCount ?? 0);

        // Get unique cities from RMCs using cityName
        const uniqueCities =
          result.items?.reduce((acc: City[], rmc: any) => {
            if (rmc.cityName) {
              const exists = acc.some((city) => city.name === rmc.cityName);
              if (!exists) {
                acc.push({
                  id: rmc.cityId,
                  name: rmc.cityName,
                } as City);
              }
            }
            return acc;
          }, []) || [];

        setRmcCities(uniqueCities);
      }
      setRmcsLoaded(true);
    } catch (error) {
      console.error("Error loading paginated RMC data:", error);
      setRmcs([]);
      setRmcCities([]);
      setTotalRecords(0);
    } finally {
      setLoadingRmcs(false);
    }
  };

  const loadRmcDataFull = async () => {
    setLoadingRmcs(true);
    try {
      const data = await cachedMasterDataService.getRmcList();
      setRmcs(data ?? []);
      setTotalRecords(data?.length ?? 0);

      // Get unique cities from RMCs using cityName
      const uniqueCities =
        data?.reduce((acc: City[], rmc) => {
          if (rmc.cityName) {
            const exists = acc.some((city) => city.name === rmc.cityName);
            if (!exists) {
              acc.push({
                id: rmc.cityId,
                name: rmc.cityName,
              } as City);
            }
          }
          return acc;
        }, []) || [];

      setRmcCities(uniqueCities);
      setRmcsLoaded(true);
    } catch (error) {
      console.error("Error loading full RMC data:", error);
      setRmcs([]);
      setRmcCities([]);
      setTotalRecords(0);
    } finally {
      setLoadingRmcs(false);
    }
  };

  const loadRmcData = async () => {
    if (usePagination) {
      await loadRmcDataPaginated();
    } else {
      await loadRmcDataFull();
    }
  };

  useEffect(() => {
    // Load data when component mounts or pagination mode changes
    if (!rmcsLoaded && !loadingRmcs) {
      loadRmcData();
    }
  }, [rmcsLoaded, loadingRmcs]);

  // Reload when pagination mode changes
  useEffect(() => {
    if (rmcsLoaded) {
      setRmcsLoaded(false); // Trigger reload
    }
  }, [usePagination]);
  
  // Reload data when Type filter changes
  useEffect(() => {
    if (!usePagination) return; // Only for paginated mode
    
    // Reset to first page when Type filter changes
    const newState = {
      ...lazyState,
      first: 0,
      page: 0,
    };
    setLazyState(newState);
    
    // Reload data with Type filter
    loadRmcDataPaginated(
      newState.page,
      newState.rows,
      newState.sortField,
      newState.sortOrder,
      globalFilter
    );
  }, [filterTypeValue]);

  const fetchCountries = async () => {
    const result = await countryService.getAll();
    const mapped = result.map((c: any) => ({ label: c.name, value: c.id }));
    setCountries(mapped);
    return result;
  };
  const handleCountryChange = async (countryId: number) => {
    setSelectedCountry(countryId);
    setSelectedState(null);
    setCities([]);
    setForm((prev) => ({ ...prev, cityId: 0 })); // reset cityId
    try {
      const result = await statesService.getByCountryId(countryId);
      setStates(result.map((s: any) => ({ label: s.name, value: s.id })));
    } catch {
      setStates([]);
    }
  };

  const handleStateChange = async (stateId: number) => {
    setSelectedState(stateId);
    setForm((prev) => ({ ...prev, cityId: 0 }));
    try {
      const result = await citiesService.getByStateId(stateId);
      setCities(result ?? []);
    } catch {
      setCities([]);
    }
  };

  // Autocomplete search functions
  const searchCountry = (event: { query: string }) => {
    setCountryInputText(event.query); // Update input text as user types
    const filtered = countries.filter((country) =>
      country.label.toLowerCase().includes(event.query.toLowerCase())
    );
    setFilteredCountries(filtered);
  };

  const searchState = (event: { query: string }) => {
    setStateInputText(event.query); // Update input text as user types
    const filtered = states.filter((state) =>
      state.label.toLowerCase().includes(event.query.toLowerCase())
    );
    setFilteredStates(filtered);
  };

  const searchCity = (event: { query: string }) => {
    setCityInputText(event.query); // Update input text as user types
    const filtered = cities.filter((city) =>
      city.name.toLowerCase().includes(event.query.toLowerCase())
    );
    setFilteredCities(filtered);
  };

  // Sync input text states with selected values
  useEffect(() => {
    const selectedCountryObj = countries.find(
      (c) => c.value === selectedCountry
    );
    setCountryInputText(selectedCountryObj?.label || "");
  }, [selectedCountry, countries]);

  useEffect(() => {
    const selectedStateObj = states.find((s) => s.value === selectedState);
    setStateInputText(selectedStateObj?.label || "");
  }, [selectedState, states]);

  useEffect(() => {
    const selectedCityObj = cities.find((c) => c.id === form.cityId);
    setCityInputText(selectedCityObj?.name || "");
  }, [form.cityId, cities]);

  // Clear input texts when drawer closes or mode changes
  useEffect(() => {
    if (!showDrawer) {
      setCountryInputText("");
      setStateInputText("");
      setCityInputText("");
    }
  }, [showDrawer]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    if (e.target.name === "name") {
      // Allow all common symbols and characters except < > \ / which might cause issues
      const value = e.target.value.replace(/[<>\\\/]/g, "");
      setForm({ ...form, [e.target.name]: value });
      setErrors({ ...errors, [e.target.name]: "" });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.name.trim()) newErrors.name = "Name is required";
    else if (/[<>\\\/]/.test(form.name.trim()))
      newErrors.name = "Name cannot contain the following symbols: < > \\ /";
    if (!type) newErrors.type = "Type is required";
    if (!form.cityId || form.cityId === 0)
      newErrors.cityId = "City is required";
    // Add more validations if needed
    return newErrors;
  };

  // Check if required fields are filled for Save button enable/disable
  const isFormValid = (): boolean => {
    // Required fields: name, type, and cityId
    return !!(
      form.name &&
      form.name.trim().length > 0 &&
      type &&
      form.cityId &&
      form.cityId > 0
    );
  };

  const handleSave = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    try {
      const formToSend = {
        name: form.name,
        code: form.code,
        description: form.description,
        address: form.address,
        cityId: selectedCity?.id ? Number(selectedCity.id) : form.cityId,
        zipCode: form.zipCode,
        aggregated: type === "Aggregator",
      };

      if (editingRmcId) {
        // Update existing RMC
        const updated = await rmcService.update(editingRmcId, formToSend);

        // Handle city update immediately
        const updatedWithCity = {
          ...updated,
          cityName: formToSend.cityId
            ? cities.find((c) => c.id === formToSend.cityId)?.name
            : undefined,
        };

        setRmcs((prev) =>
          prev.map((r) => (r.id === editingRmcId ? updatedWithCity : r))
        );
        cachedMasterDataService.clearRmcCache();

        // Refresh RMC list to ensure we have latest data
        cachedMasterDataService.refreshRmcList().catch(console.error);
      } else {
        const newRmc = await rmcService.create(formToSend);

        // Handle city for new RMC
        const newRmcWithCity = {
          ...newRmc,
          cityName: formToSend.cityId
            ? cities.find((c) => c.id === formToSend.cityId)?.name
            : undefined,
        };

        setRmcs([...rmcs, newRmcWithCity]);
        cachedMasterDataService.clearRmcCache();

        // Refresh RMC list for consistency
        cachedMasterDataService.refreshRmcList().catch(console.error);
      }

      setForm({
        name: "",
        code: "",
        description: "",
        address: "",
        cityId: 0,
        zipCode: "",
        aggregated: false,
      });
      setType("RMC");
      setEditingRmcId(null);
      setShowPopup(false);
      setShowDrawer(false);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: editingRmcId
          ? "RMC updated successfully."
          : "RMC created successfully.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch (error) {
      console.error("Failed to save RMC:", error);
      alert("Failed to save RMC. Please check input or try again later.");
    }
  };

  const handleRowClick = (params: any) => {
    const { id, name } = params.row;
    if (!id) return;
    navigate(`/rmc/${id}/clients`, { state: { rmcName: name } });
  };

  const handleViewClick = async (rmc: any) => {
    console.log("=== RMC View Data ===");
    console.log("Full RMC object:", rmc);
    console.log("Created By Name:", rmc.createdByName);
    console.log("Modified By Name:", rmc.modifiedByName);
    console.log("Created By:", rmc.createdBy);
    console.log("Modified By:", rmc.modifiedBy);
    console.log("===================");

    setEditingRmcId(rmc.id ?? rmc.Id ?? null);
    setForm({
      name: rmc.name || "",
      code: rmc.code || "",
      description: rmc.description || "",
      address: rmc.address || "",
      cityId: rmc.cityId ?? rmc.CityId ?? 0,
      zipCode: rmc.zipCode || "",
      aggregated: (rmc as any).aggregated ?? false,
    });
    setType((rmc as any).aggregated ? "Aggregator" : "RMC");
    setViewCityName(rmc.cityName || "");

    try {
      await fetchCountries();
      const countryId =
        (rmc as any).countryId ?? (rmc as any).CountryId ?? null;
      const stateId = (rmc as any).stateId ?? (rmc as any).StateId ?? null;

      if (countryId) {
        await handleCountryChange(Number(countryId));
        setSelectedCountry(Number(countryId));
      }

      if (stateId) {
        await handleStateChange(Number(stateId));
        setSelectedState(Number(stateId));
      }
    } catch (err) {
      console.error("Error preloading data for view", err);
    }

    setDrawerMode("view");
    setShowDrawer(true);
  };

  const handleEditClick = async (rmc: any) => {
    setEditingRmcId(rmc.id ?? rmc.Id ?? null);
    setForm({
      name: rmc.name || "",
      code: rmc.code || "",
      description: rmc.description || "",
      address: rmc.address || "",
      cityId: 0,
      zipCode: rmc.zipCode || "",
      aggregated: (rmc as any).aggregated ?? false,
    });
    setType((rmc as any).aggregated ? "Aggregator" : "RMC");

    try {
      // ensure countries loaded
      await fetchCountries();

      const countryId =
        (rmc as any).countryId ?? (rmc as any).CountryId ?? null;
      const stateId = (rmc as any).stateId ?? (rmc as any).StateId ?? null;
      const cityId = (rmc as any).cityId ?? (rmc as any).CityId ?? null;

      if (countryId) {
        await handleCountryChange(Number(countryId));
        setSelectedCountry(Number(countryId));
      }

      if (stateId) {
        await handleStateChange(Number(stateId));
        setSelectedState(Number(stateId));
      }

      if (cityId) {
        setForm((prev) => ({ ...prev, cityId: Number(cityId) }));
      }
    } catch (err) {
      console.error("Error preloading selects for edit", err);
    }

    setErrors({});
    setDrawerMode("edit");
    setShowDrawer(true);
  };

  const handleCreateClick = () => {
    setEditingRmcId(null);
    setForm({
      name: "",
      code: "",
      description: "",
      address: "",
      cityId: 0,
      zipCode: "",
      aggregated: false,
    });
    setType("RMC");
    setViewCityName("");
    setSelectedCountry(null);
    setSelectedState(null);
    fetchCountries();
    setErrors({});
    setDrawerMode("create");
    setShowDrawer(true);
  };

  const handleExportToExcel = async () => {
    setIsExporting(true);
    try {
      // Fetch full list without pagination
      const allData = await cachedMasterDataService.getRmcList();

      if (!allData || allData.length === 0) {
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "warning",
          title: "No data to export",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
        return;
      }

      // Prepare data for export
      const exportData = allData.map((rmc: any) => ({
        Name: rmc.name || "",
        Code: rmc.code || "",
        Description: rmc.description || "",
        Address: rmc.address || "",
        City: rmc.cityName || "",
        ZipCode: rmc.zipCode || "",
        Type: rmc.aggregated ? "Aggregator" : "RMC",
        CreatedBy: rmc.createdByName || "",
        CreatedDate: rmc.createdDate
          ? new Date(rmc.createdDate).toLocaleDateString()
          : "",
        ModifiedBy: rmc.modifiedByName || "",
        ModifiedDate: rmc.modifiedDate
          ? new Date(rmc.modifiedDate).toLocaleDateString()
          : "",
      }));

      // Import xlsx dynamically
      const XLSX = await import("xlsx");
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "RMCs");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `RMC_Export_${timestamp}.xlsx`;

      // Save file
      XLSX.writeFile(workbook, filename);

      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "success",
        title: `Exported ${allData.length} records successfully`,
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to export data. Please try again.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        customClass: { popup: "swal2-toast-bottom" },
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteClick = async (rmc: any) => {
    const rmcId = rmc.id ?? rmc.Id;
    const rmcName = rmc.name || "this RMC";

    const result = await Swal.fire({
      title: "Are you sure?",
      html: `Do you want to delete <strong>${rmcName}</strong>?<br/>This action will mark it as inactive.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const success = await rmcService.updateStatus(rmcId);
        if (success) {
          // Refresh the RMC list
          await loadRmcData();

          Swal.fire({
            toast: true,
            position: "bottom",
            icon: "success",
            title: "RMC deleted successfully.",
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: "swal2-toast-bottom" },
          });
        } else {
          throw new Error("Failed to delete RMC");
        }
      } catch (error) {
        console.error("Failed to delete RMC:", error);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "error",
          title: "Failed to delete RMC. Please try again.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          customClass: { popup: "swal2-toast-bottom" },
        });
      }
    }
  };

  // Custom body template for the Name column
  const nameBodyTemplate = (rowData: any) => (
    <span
      className="text-link"
      style={{ cursor: "pointer" }}
      onClick={() => handleRowClick({ row: rowData })}
    >
      {rowData.name}
    </span>
  );

  // Custom body template for the Description column with tooltip
  const descriptionBodyTemplate = (rowData: any) => {
    const description = rowData.description || "";
    const uniqueClass = `desc-tooltip-${rowData.id}`;
    return (
      <>
        <Tooltip
          target={`.${uniqueClass}`}
          position="top"
          className="desc-tooltip-target"
        />
        <div
          className={`rmc-description-cell ${uniqueClass}`}
          data-pr-tooltip={description}
        >
          {description}
        </div>
      </>
    );
  };

  // Custom body template for the Address column with tooltip
  const addressBodyTemplate = (rowData: any) => {
    const address = rowData.address || "";
    const uniqueClass = `addr-tooltip-${rowData.id}`;
    return (
      <>
        <Tooltip
          target={`.${uniqueClass}`}
          position="top"
          className="addr-tooltip-target"
        />
        <div
          className={`rmc-description-cell ${uniqueClass}`}
          data-pr-tooltip={address}
        >
          {address}
        </div>
      </>
    );
  };

  // Custom body template for the Type column
  const typeBodyTemplate = (rowData: any) => {
    return <span>{rowData.aggregated ? "Aggregator" : "RMC"}</span>;
  };

  // Custom body template for the City column
  const cityBodyTemplate = (rowData: any) => {
    return <span>{rowData.cityName || ""}</span>;
  };

  // Reusable function to format dates
  const formatAuditDate = (date: any): string => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  // Reusable function to generate audit information text
  const getAuditInfoText = (
    record: any,
    recordTypeName: string = "record"
  ): string => {
    const recordName = record.name || `this ${recordTypeName}`;
    const createdBy = record.createdBy;
    const modifiedBy = record.modifiedBy;
    const createdByName = record.createdByName;
    const modifiedByName = record.modifiedByName;
    const createdDate = record.createdDate;
    const modifiedDate = record.modifiedDate;

    if (!createdDate) return "";

    // Check if the same person created and modified
    const isSameUser = createdBy && modifiedBy && createdBy === modifiedBy;

    let text = `The ${recordTypeName} ${recordName} was created`;

    if (createdByName) {
      text += ` by ${createdByName}`;
    }

    text += ` on ${formatAuditDate(createdDate)}`;

    if (modifiedDate) {
      if (isSameUser) {
        text += ` and last modified on ${formatAuditDate(modifiedDate)}`;
      } else if (modifiedByName) {
        text += ` and ${modifiedByName} made edits on ${formatAuditDate(
          modifiedDate
        )}`;
      } else {
        text += ` and last modified on ${formatAuditDate(modifiedDate)}`;
      }
    }

    return text;
  };

  // Reusable component for rendering audit information
  const renderAuditInfo = (record: any, recordTypeName: string = "record") => {
    const auditText = getAuditInfoText(record, recordTypeName);

    if (!auditText) return null;

    // Extract the record name for bold formatting
    const recordName = record.name || `this ${recordTypeName}`;
    const parts = auditText.split(recordName);

    return (
      <div className="audit-info-text">
        {parts[0]}
        <strong>{recordName}</strong>
        {parts[1]}
      </div>
    );
  };

  // Custom body template for Actions column with audit info icon
  const actionsBodyTemplate = (rowData: any) => {
    const auditTooltipClass = `audit-tooltip-${rowData.id}`;
    const auditText = getAuditInfoText(rowData, "RMC");

    return (
      <>
        <Tooltip
          target={`.${auditTooltipClass}`}
          position="left"
          className="audit-info-tooltip"
        />
        <div className="d-flex gap-2 align-items-center">
          <Button
            icon="pi pi-eye"
            rounded
            severity="info"
            onClick={(e) => {
              e.stopPropagation();
              handleViewClick(rowData);
            }}
            tooltip="View Details"
            tooltipOptions={{ position: "top" }}
          />
          <Button
            icon="pi pi-pencil"
            rounded
            severity="warning"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(rowData);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: "top" }}
          />
          <Button
            icon="pi pi-trash"
            rounded
            severity="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(rowData);
            }}
            tooltip="Delete"
            tooltipOptions={{ position: "top" }}
          />
          {auditText && (
            <Button
              icon="pi pi-info-circle"
              rounded
              severity="info"
              className={auditTooltipClass}
              data-pr-tooltip={auditText}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
        </div>
      </>
    );
  };

  // Memoize RMC mapping for better performance and immediate city updates
  const mappedRmcs = React.useMemo(() => {
    return rmcs.map((rmc) => {
      let cityLabel: string = rmc.cityName || ""; // Initialize with empty string

      if (!cityLabel && rmc.cityId) {
        // Find city name from local cities list
        const cityObj = cities.find((c) => c.id === rmc.cityId);
        cityLabel = cityObj?.name || `City ID: ${rmc.cityId}`;
      }

      if (!cityLabel) {
        cityLabel = "No City";
      }

      const typeLabel = rmc.aggregated ? "Aggregator" : "RMC";
      return { ...rmc, city: cityLabel, type: typeLabel };
    });
  }, [rmcs, cities]);

  // Compute available Type options based on current data (only show types present)
  const typeOptions = React.useMemo(() => {
    const set = new Set<string>();
    rmcs.forEach((r) => {
      set.add(r.aggregated ? "Aggregator" : "RMC");
    });
    const opts: { label: string; value: string }[] = [];
    if (set.has("RMC")) opts.push({ label: "RMC", value: "RMC" });
    if (set.has("Aggregator")) opts.push({ label: "Aggregator", value: "Aggregator" });
    return opts;
  }, [rmcs]);

    // Then apply filters
  const filteredRows = mappedRmcs.filter((rmc) => {
    let shouldInclude = true;

    // Apply city filter first if selected
    if (selectedCity) {
      if (!rmc.cityName || rmc.cityName.toLowerCase() !== selectedCity.name.toLowerCase()) {
        return false;
      }
    }

    // Apply RMC filter if selected
    if (selectedRmc) {
      if (rmc.id !== selectedRmc.id) {
        return false;
      }
    }

    // Apply type filter if selected
    if (selectedType) {
      const currentIsAggregator = Boolean(rmc.aggregated);
      const wantAggregator = selectedType.value === "Aggregator";
      if (currentIsAggregator !== wantAggregator) {
        return false;
      }
    }

    // Apply global filter last
    if (globalFilter) {
      const searchText = globalFilter.toLowerCase();
      shouldInclude = (
        rmc.name?.toLowerCase().includes(searchText) ||
        rmc.description?.toLowerCase().includes(searchText) ||
        rmc.cityName?.toLowerCase().includes(searchText) ||
        (rmc.aggregated ? 'aggregator' : 'rmc').toLowerCase().includes(searchText)
      );
    }

    return shouldInclude;
  });  const rows = filteredRows;
  const [filterText, setFilterText] = React.useState("");
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false);
  const filteredItems = rows.filter(
    (item) =>
      item.name && item.name.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <>
      <div className="content">
        {/* <div className="content-header">
          <div className='container d-flex align-items-center justify-content-between'>
            <div className='d-flex flex-column'>
              <h6 className="_600 mb-2 page-heading" >
                Relocation Management Companies
              </h6>

            A descriptive text about title

            <p className='lead tag-line tx-16 mb-0' >A descriptive text about title</p>
            </div>

          </div>
        </div> */}
        <PageHeader
          title="Relocation Management Companies"
          showBackButton={false}
        />

        <div className="content-body">
          <div className="container-fluid mt-2">
            <div className="row">
              <div className="col-md-12 mb-2">
                <div className="rmc-grid-container">
                  <DataTable
                    stripedRows
                    className="grid-table"
                    value={
                      selectedType 
                        ? rmcs.filter(rmc => selectedType.value === "Aggregator" ? Boolean(rmc.aggregated) === true : Boolean(rmc.aggregated) === false) 
                        : selectedRmc 
                          ? [selectedRmc] 
                          : selectedCity 
                            ? rmcs.filter(rmc => rmc.cityName === selectedCity.name) 
                            : (usePagination ? rmcs : filteredRows)
                    }
                    lazy={usePagination && !selectedRmc && !selectedCity && !selectedType}
                    paginator={!selectedRmc && !selectedCity && !selectedType}
                    rows={lazyState.rows}
                    first={lazyState.first}
                    totalRecords={
                      selectedRmc ? 1 : 
                      selectedCity ? rmcs.filter(rmc => rmc.cityName === selectedCity.name).length :
                      selectedType ? rmcs.filter(rmc => 
                        selectedType.value === "Aggregator" ? rmc.aggregated : !rmc.aggregated
                      ).length :
                      (usePagination ? totalRecords : filteredRows.length)
                    }
                    onPage={(e) => {
                      if (usePagination) {
                        const newState = {
                          ...lazyState,
                          first: e.first,
                          rows: e.rows,
                          page: e.first / e.rows,
                        };
                        setLazyState(newState);
                        loadRmcDataPaginated(
                          newState.page,
                          newState.rows,
                          newState.sortField,
                          newState.sortOrder,
                          globalFilter
                        );
                      } else {
                        const newPage = e.first / e.rows;
                        setCurrentPage(newPage);
                        setRowsPerPage(e.rows);
                        localStorage.setItem(
                          "rmcTablePage",
                          newPage.toString()
                        );
                        localStorage.setItem("rmcTableRows", e.rows.toString());
                      }
                    }}
                    onSort={(e) => {
                      if (usePagination && e.sortField) {
                        const newState = {
                          ...lazyState,
                          sortField: e.sortField as string,
                          sortOrder: e.sortOrder ?? 1,
                        };
                        setLazyState(newState);
                        loadRmcDataPaginated(
                          newState.page,
                          newState.rows,
                          newState.sortField,
                          newState.sortOrder,
                          globalFilter
                        );
                      }
                    }}
                    sortField={usePagination ? lazyState.sortField : undefined}
                    sortOrder={
                      usePagination
                        ? (lazyState.sortOrder as 1 | -1 | 0 | null | undefined)
                        : undefined
                    }
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    sortMode={usePagination ? "single" : "multiple"}
                    emptyMessage="No RMCs found."
                    onRowClick={(e) => handleRowClick({ row: e.data })}
                    dataKey="id"
                    globalFilter={usePagination ? undefined : globalFilter}
                    loading={loadingRmcs}
                    header={
                      <div className="d-flex justify-content-between align-items-end gap-3">
                        <div className="rmc-filter-container">
                          <div className="rmc-filter-item">
                            <IconField iconPosition="left">
                              <InputIcon>
                                <Icon.Search className="tx-12" />
                              </InputIcon>
                              <InputText
                                type="text"
                                value={globalFilter}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setGlobalFilter(value);
                                  setSelectedRmc(null);
                                  setSelectedCity(null);
                                  setSelectedType(null);
                                  setCurrentPage(0);
                                  localStorage.setItem("rmcTablePage", "0");

                                  // If using pagination, trigger server-side search after typing stops
                                  if (usePagination) {
                                    const newState = {
                                      ...lazyState,
                                      first: 0,
                                      page: 0,
                                    };
                                    setLazyState(newState);
                                    // Use setTimeout to debounce the search
                                    setTimeout(() => {
                                      loadRmcDataPaginated(
                                        newState.page,
                                        newState.rows,
                                        newState.sortField,
                                        newState.sortOrder,
                                        value
                                      );
                                    }, 500);
                                  }
                                }}
                                placeholder="Search"
                                style={{ width: "200px", height: "38px" }}
                              />
                            </IconField>
                          </div>

                          <div className="rmc-filter-item">
                            <Dropdown
                              value={selectedRmc}
                              onChange={(e) => {
                                setSelectedRmc(e.value);
                                // Clear other filters and search when RMC is selected
                                setGlobalFilter("");
                                setSelectedCity(null);
                                setSelectedType(null);
                                // Reset pagination
                                setCurrentPage(0);
                                setLazyState(prev => ({
                                  ...prev,
                                  first: 0,
                                  page: 0
                                }));
                              }}
                              options={rmcs}
                              optionLabel="name"
                              placeholder="Select RMC"
                              style={{ width: "200px", height: "38px" }}
                              filter
                              showClear
                              appendTo="self"
                            />
                          </div>

                          <div className="rmc-filter-item">
                            <Dropdown
                              value={selectedCity}
                              onChange={(e) => {
                                const selectedCity = e.value;
                                setSelectedCity(selectedCity);
                                setGlobalFilter("");
                                setSelectedRmc(null);
                                setSelectedType(null);
                                // Reset pagination
                                setCurrentPage(0);
                                if (selectedCity && !usePagination) {
                                  // For non-paginated mode, filter directly
                                  const cityRmcs = rmcs.filter(
                                    rmc => rmc.cityName === selectedCity.name
                                  );
                                  setRmcs(cityRmcs);
                                } else {
                                  setLazyState(prev => ({
                                    ...prev,
                                    first: 0,
                                    page: 0
                                  }));
                                }
                              }}
                              options={rmcCities}
                              optionLabel="name"
                              placeholder="Select City"
                              style={{ width: "200px", height: "38px" }}
                              filter
                              showClear
                              appendTo="self"
                            />
                          </div>

                          <div className="rmc-filter-item">
                            <Dropdown
                              value={selectedType}
                              onChange={async (e) => {
                                const selectedTypeValue = e.value;
                                
                                // Update selected type state
                                setSelectedType(selectedTypeValue);
                                setFilterTypeValue(selectedTypeValue?.value || null);
                                
                                // Clear other filters to avoid conflicts
                                setSelectedRmc(null);
                                setSelectedCity(null);
                                setGlobalFilter("");
                                
                                // Reset pagination
                                setCurrentPage(0);
                                
                                if (usePagination) {
                                  // For paginated mode
                                  const newState = {
                                    ...lazyState,
                                    first: 0,
                                    page: 0
                                  };
                                  setLazyState(newState);
                                  
                                  // Prepare filters including type
                                  const filters: {
                                    pageNumber: number;
                                    pageSize: number;
                                    sortField: string;
                                    sortOrder: string;
                                    isActive: boolean;
                                    aggregated?: boolean;
                                  } = {
                                    pageNumber: 1,
                                    pageSize: newState.rows,
                                    sortField: newState.sortField,
                                    sortOrder: newState.sortOrder === 1 ? "asc" : "desc",
                                    isActive: true,
                                    aggregated: true, // default
                                  };
                                  
                                  if (selectedTypeValue) {
                                    filters.aggregated = selectedTypeValue.value === "Aggregator" ? true : false;
                                  }
                                  
                                  setLoadingRmcs(true);
                                  try {
                                    const result = await rmcService.getAllPaginated<Rmc>(filters);
                                    if (result) {
                                      setRmcs(result.items ?? []);
                                      setTotalRecords(result.totalCount ?? 0);
                                      
                                      // Update city list based on filtered data
                                      const uniqueCities = result.items?.reduce((acc: City[], rmc: any) => {
                                        if (rmc.cityName && !acc.some(city => city.name === rmc.cityName)) {
                                          acc.push({
                                            id: rmc.cityId,
                                            name: rmc.cityName,
                                          } as City);
                                        }
                                        return acc;
                                      }, []) || [];
                                      setRmcCities(uniqueCities);
                                    }
                                  } catch (error) {
                                    console.error("Error loading filtered RMC data:", error);
                                  } finally {
                                    setLoadingRmcs(false);
                                  }
                                } else {
                                  // For non-paginated mode
                                  setLoadingRmcs(true);
                                  try {
                                    // Load full data first
                                    const data = await cachedMasterDataService.getRmcList();
                                    
                                    if (selectedTypeValue) {
                                      // Filter for selected type
                                      const filteredData = data.filter(rmc => 
                                        selectedTypeValue.value === "Aggregator" ? rmc.aggregated : !rmc.aggregated
                                      );
                                      setRmcs(filteredData);
                                      setTotalRecords(filteredData.length);
                                      
                                      // Update city list based on filtered data
                                      const uniqueCities = filteredData.reduce((acc: City[], rmc) => {
                                        if (rmc.cityName && !acc.some(city => city.name === rmc.cityName)) {
                                          acc.push({
                                            id: rmc.cityId,
                                            name: rmc.cityName,
                                          } as City);
                                        }
                                        return acc;
                                      }, []);
                                      setRmcCities(uniqueCities);
                                    } else {
                                      // No type filter, show all data
                                      setRmcs(data);
                                      setTotalRecords(data.length);
                                    }
                                  } catch (error) {
                                    console.error("Error loading RMC data:", error);
                                  } finally {
                                    setLoadingRmcs(false);
                                  }
                                }
                              }}
                              options={typeOptions}
                              placeholder="Select Type"
                              style={{ width: "200px", height: "38px" }}
                              showClear
                              appendTo="self"
                            />
                          </div>
                        </div>

                        <div className="rmc-action-buttons">
                          <button
                            type="button"
                            className="rmc-modern-btn btn btn-outline-primary"
                            onClick={() => setShowImportDialog(true)}
                            disabled={loadingRmcs}
                          >
                            <Icon.FileEarmarkArrowUp />
                            Import from Excel
                          </button>
                          <button
                            type="button"
                            className="rmc-modern-btn btn btn-outline-success"
                            onClick={handleExportToExcel}
                            disabled={isExporting || loadingRmcs}
                          >
                            {isExporting ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Exporting...
                              </>
                            ) : (
                              <>
                                <Icon.FileEarmarkSpreadsheet />
                                Export to Excel
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="rmc-modern-btn btn btn-primary"
                            onClick={handleCreateClick}
                          >
                            <Icon.PlusCircle /> Create RMC
                          </button>
                        </div>
                      </div>
                    }
                  >
                    <Column
                      field="name"
                      header="Name"
                      sortable
                      body={nameBodyTemplate}
                      style={{ width: "200px", minWidth: "200px" }}
                    />
                    {/* <Column field="code" header="Code" sortable /> */}
                    <Column
                      field="description"
                      header="Description"
                      sortable
                      body={descriptionBodyTemplate}
                      style={{ width: "350px", minWidth: "300px" }}
                    />
                    <Column
                      field="address"
                      header="Address"
                      sortable
                      body={addressBodyTemplate}
                      style={{ width: "300px", minWidth: "250px" }}
                    />
                    <Column
                      field="cityName"
                      header="City"
                      sortable
                      body={cityBodyTemplate}
                      style={{ width: "120px" }}
                    />
                    <Column
                      field="zipCode"
                      header="ZipCode"
                      sortable
                      style={{ width: "100px" }}
                    />
                    {/* Add Type column */}
                    <Column
                      field="aggregated"
                      header="Type"
                      sortable
                      body={typeBodyTemplate}
                      style={{ width: "120px" }}
                    />
                    <Column
                      header="Actions"
                      body={actionsBodyTemplate}
                      style={{
                        width: "170px",
                        minWidth: "170px",
                        maxWidth: "170px",
                      }}
                    />
                  </DataTable>
                </div>
              </div>
              {/* <div className="col-md-12 mb-3">
                <DataGrid
                  rows={filteredItems}
                  columns={columns}
                  paginationModel={{ pageSize: 10, page: 0 }}
                  onPaginationModelChange={(model) => {
                    // optional: handle external pagination state here
                  }}
                  pageSizeOptions={[5, 10, 20]}
                  pagination
                  onRowClick={handleRowClick}
                />
              </div> */}
            </div>
          </div>
        </div>
      </div>
      {/* Right Drawer for View/Edit/Create */}
      <Offcanvas
        show={showDrawer}
        onHide={() => {
          setShowDrawer(false);
          setErrors({});
          setViewCityName("");
        }}
        placement="end"
        className="rmc-drawer"
        style={{ width: "500px" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {drawerMode === "view" && "RMC Details"}
            {drawerMode === "edit" && "Edit RMC"}
            {drawerMode === "create" && "Create RMC"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="row g-2">
            {/* Name (70%) and Type (30%) side by side */}
            <div className="col-8 form-group">
              <Form.Label>
                RMC Name{" "}
                {drawerMode !== "view" && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">{form.name || "-"}</div>
              ) : (
                <>
                  <Form.Control
                    name="name"
                    placeholder="Name"
                    value={form.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                  />
                  {errors.name && (
                    <div className="text-danger tx-13">{errors.name}</div>
                  )}
                </>
              )}
            </div>

            <div className="col-4 form-group">
              <Form.Label>
                Type{" "}
                {drawerMode !== "view" && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">{type || "-"}</div>
              ) : (
                <>
                  <Form.Select
                    name="type"
                    value={type ? type : 0}
                    onChange={(e) => {
                      const value = e.target.value;
                      setType(
                        value === "0" ? "" : (value as "RMC" | "Aggregator")
                      );
                      setErrors({ ...errors, type: "" });
                    }}
                    className="form-select"
                    isInvalid={!!errors.type}
                  >
                    <option value={0}>Select Type</option>
                    <option value="RMC">RMC</option>
                    <option value="Aggregator">Aggregator</option>
                  </Form.Select>
                  {errors.type && (
                    <div className="text-danger tx-13">{errors.type}</div>
                  )}
                </>
              )}
            </div>

            <div className="col-12 form-group">
              <Form.Label>Description</Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext preserve-whitespace">
                  {form.description || "-"}
                </div>
              ) : (
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  placeholder="Description"
                  value={form.description}
                  onChange={handleChange}
                />
              )}
            </div>

            <div className="col-12 form-group">
              <Form.Label>Address</Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">
                  {form.address || "-"}
                </div>
              ) : (
                <Form.Control
                  name="address"
                  placeholder="Address"
                  value={form.address}
                  onChange={handleChange}
                />
              )}
            </div>

            <div className="col-6 form-group">
              <Form.Label>Country</Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">
                  {countries.find((c) => c.value === selectedCountry)?.label ||
                    "-"}
                </div>
              ) : (
                <SharedAutoComplete
                  id="rmc-country-autocomplete"
                  value={countryInputText}
                  suggestions={filteredCountries}
                  completeMethod={searchCountry}
                  field="label"
                  placeholder="Select Country"
                  onChange={(e: any) => {
                    // Handle text input or selection
                    if (typeof e.value === "string") {
                      setCountryInputText(e.value);
                    } else if (
                      e.value &&
                      typeof e.value === "object" &&
                      "value" in e.value
                    ) {
                      handleCountryChange(e.value.value as number);
                      setCountryInputText(e.value.label);
                    }
                  }}
                  onSelect={(e: any) => {
                    // Handle when item is selected from suggestions
                    if (e.value && "value" in e.value) {
                      handleCountryChange(e.value.value as number);
                      setCountryInputText(e.value.label);
                    }
                  }}
                />
              )}
            </div>

            <div className="col-6 form-group">
              <Form.Label>State</Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">
                  {states.find((s) => s.value === selectedState)?.label || "-"}
                </div>
              ) : (
                <SharedAutoComplete
                  id="rmc-state-autocomplete"
                  value={stateInputText}
                  suggestions={filteredStates}
                  completeMethod={searchState}
                  field="label"
                  placeholder="Select State"
                  onChange={(e: any) => {
                    // Handle text input or selection
                    if (typeof e.value === "string") {
                      setStateInputText(e.value);
                    } else if (
                      e.value &&
                      typeof e.value === "object" &&
                      "value" in e.value
                    ) {
                      handleStateChange(e.value.value as number);
                      setStateInputText(e.value.label);
                    }
                  }}
                  onSelect={(e: any) => {
                    // Handle when item is selected from suggestions
                    if (e.value && "value" in e.value) {
                      handleStateChange(e.value.value as number);
                      setStateInputText(e.value.label);
                    }
                  }}
                  disabled={!selectedCountry}
                />
              )}
            </div>

            <div className="col-6 form-group">
              <Form.Label>
                City{" "}
                {drawerMode !== "view" && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">
                  {viewCityName || "-"}
                </div>
              ) : (
                <>
                  <SharedAutoComplete
                    id="rmc-city-autocomplete"
                    value={cityInputText}
                    suggestions={filteredCities}
                    completeMethod={searchCity}
                    field="name"
                    placeholder="Select City"
                    onChange={(e: any) => {
                      // Handle text input or selection
                      if (typeof e.value === "string") {
                        setCityInputText(e.value);
                      } else if (
                        e.value &&
                        typeof e.value === "object" &&
                        "id" in e.value
                      ) {
                        setForm({ ...form, cityId: e.value.id as number });
                        setCityInputText(e.value.name);
                        setErrors({ ...errors, cityId: "" });
                      }
                    }}
                    onSelect={(e: any) => {
                      // Handle when item is selected from suggestions
                      if (e.value && "id" in e.value) {
                        setForm({ ...form, cityId: e.value.id as number });
                        setCityInputText(e.value.name);
                        setErrors({ ...errors, cityId: "" });
                      }
                    }}
                    disabled={!selectedState}
                    error={errors.cityId}
                  />
                  {errors.cityId && (
                    <div className="text-danger tx-13">{errors.cityId}</div>
                  )}
                </>
              )}
            </div>

            <div className="col-6 form-group">
              <Form.Label>Zip Code</Form.Label>
              {drawerMode === "view" ? (
                <div className="form-control-plaintext">
                  {form.zipCode || "-"}
                </div>
              ) : (
                <Form.Control
                  name="zipCode"
                  placeholder="Zip Code"
                  value={form.zipCode}
                  onChange={(e) => {
                    // Only allow numeric values and max 6 digits
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setForm({ ...form, zipCode: value });
                  }}
                  inputMode="numeric"
                  maxLength={6}
                />
              )}
            </div>

            {drawerMode === "view" && editingRmcId && (
              <div className="col-12 mt-3">
                <div className="audit-info-section">
                  <div className="audit-info-title">
                    <Icon.InfoCircle size={12} className="me-1" />
                    Record Information
                  </div>
                  {(() => {
                    const currentRmc = rmcs.find((r) => r.id === editingRmcId);
                    if (!currentRmc) return null;

                    return (
                      <div className="audit-info-content">
                        {renderAuditInfo(currentRmc, "RMC")}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </Offcanvas.Body>

        {/* Drawer Footer - Like Import Excel Drawer */}
        {drawerMode === "view" ? (
          <div className="rmc-drawer-footer">
            <button
              className="btn btn-primary"
              onClick={() => {
                setDrawerMode("edit");
              }}
            >
              <Icon.Pencil className="me-2" />
              Edit RMC
            </button>
          </div>
        ) : (
          <div className="rmc-drawer-footer">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setShowDrawer(false);
                setErrors({});
                setViewCityName("");
              }}
            >
              <Icon.X className="me-2" />
              Cancel
            </button>
            <button
              className="btn btn-success"
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              <Icon.Check2 className="me-2" />
              Save
            </button>
          </div>
        )}
      </Offcanvas>

      {showPopup && (
        <Modal
          size="lg"
          show={showPopup}
          onHide={() => {
            setShowPopup(false);
            setErrors({});
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingRmcId ? "Edit RMC" : "Create RMC"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-2">
              <div className="col-6 form-gorup">
                <Form.Label>
                  RMC Name <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Control
                  name="name"
                  placeholder="Name"
                  value={form.name}
                  onChange={handleChange}
                  isInvalid={!!errors.name}
                />
                {errors.name && (
                  <div className="text-danger tx-13">{errors.name}</div>
                )}
              </div>
              <div className="col-6 form-group">
                <Form.Label>
                  Type <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Select
                  name="type"
                  value={type ? type : 0}
                  onChange={(e) => {
                    const value = e.target.value;
                    setType(
                      value === "0" ? "" : (value as "RMC" | "Aggregator")
                    );
                    setErrors({ ...errors, type: "" });
                  }}
                  className="form-select"
                  isInvalid={!!errors.type}
                >
                  <option value={0}>Select Type</option>
                  <option value="RMC">RMC</option>
                  <option value="Aggregator">Aggregator</option>
                </Form.Select>
                {errors.type && (
                  <div className="text-danger tx-13">{errors.type}</div>
                )}
              </div>
              {/* <div className="col-4 form-group">
                <Form.Label>RMC Code</Form.Label>
                <Form.Control
                  name="code"
                  placeholder="Code"
                  value={form.code}
                  onChange={handleChange}
                />
              </div> */}

              <div className="col-12 form-group">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  placeholder="Description"
                  value={form.description}
                  onChange={handleChange}
                />
              </div>
              <div className="col-12 form-group">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  name="address"
                  placeholder="Address"
                  value={form.address}
                  onChange={handleChange}
                />
              </div>
              <div className="col-6 form-group">
                <Form.Label>Country</Form.Label>
                <Form.Select
                  value={selectedCountry || ""}
                  onChange={(e) => handleCountryChange(+e.target.value)}
                  className="form-select"
                >
                  <option value="">Select Country</option>
                  {countries.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="col-6 form-group">
                <Form.Label>State</Form.Label>
                <Form.Select
                  value={selectedState || ""}
                  onChange={(e) => handleStateChange(+e.target.value)}
                  className="form-select"
                  disabled={!selectedCountry}
                >
                  <option value="">Select State</option>
                  {states.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div className="col-6 form-group">
                <Form.Label>
                  City <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Form.Select
                  name="cityId"
                  value={form.cityId || ""}
                  onChange={handleChange}
                  className="form-select"
                  isInvalid={!!errors.cityId}
                  disabled={!selectedState}
                >
                  <option value="">Select City</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Form.Select>
                {errors.cityId && (
                  <div className="text-danger tx-13">{errors.cityId}</div>
                )}
              </div>

              <div className="col-6 form-group">
                <Form.Label>Zip Code</Form.Label>
                <Form.Control
                  name="zipCode"
                  placeholder="Zip Code"
                  value={form.zipCode}
                  onChange={(e) => {
                    // Only allow numeric values and max 6 digits
                    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setForm({ ...form, zipCode: value });
                  }}
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setShowPopup(false)}
            >
              Cancel
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Import Drawer */}
      <RMCImportDrawer
        show={showImportDialog}
        onHide={() => setShowImportDialog(false)}
        onSuccess={() => {
          // Refresh the grid after successful import
          if (usePagination) {
            loadRmcDataPaginated();
          } else {
            loadRmcDataFull();
          }
          Swal.fire({
            icon: "success",
            title: "Import Successful",
            text: "RMCs have been imported successfully!",
            timer: 2000,
            showConfirmButton: false,
          });
        }}
      />
    </>
  );
};

export default RmcGrid;
